import { getClientIdByClientManger } from "rc-app/api/agencyActionsApi";
import { getAllAgent, updateAgent } from "rc-app/api/agentActionsApi";
import { getCustomerList } from "rc-app/api/customerActionsApi";
import { getClientAdminByEmailId, getSystemAdminList } from "rc-app/api/systemBusinessAdminActionApi";
import { AddButton } from "rc-app/components/Buttons";
import { CardBorder } from "rc-app/components/CardBorder";
import Icon from "rc-app/components/Icons";
import UserService from "rc-app/services/UserService";
import { toTitleCase } from "rc-app/utils";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDesktopMode } from "rc-app/hooks/useDesktopMode";
import DesktopModePrompt from "rc-app/components/DesktopModePrompt";
// import { useNavigate } from "react-router-dom";

interface Agent {
  id: string;
  agentId: string;
  agencyId?: string;
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  username: string;
  type: "Internal" | "External";
  status: "Active" | "Inactive";
  photo?: string;
  createdAt?: string;
  // System Admin (ASA) this agent is assigned to. Empty = unassigned.
  // Backed by the `asa_id` field Nivedita is adding to the agent; until then
  // every agent reads as unassigned (graceful — Assigned tab just stays empty).
  asaId?: string;
}

interface AsaOption {
  id: string;
  name: string;
}

const AgentList: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { showPrompt, accept, dismiss } = useDesktopMode();
  const [search, setSearch] = useState("");
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);

  const [loading, setLoading] = useState(true);

  // const [agentList, setAgentList] = useState([]);

  const [agents, setAgents] = useState<Agent[]>([]);
  const [asaList, setAsaList] = useState<AsaOption[]>([]);
  const [activeTab, setActiveTab] = useState<"unassigned" | "assigned">(
    "assigned",
  );
  const [assigningAgent, setAssigningAgent] = useState<Agent | null>(null);
  // agent (Mongo) id → number of leads assigned to them.
  const [leadCounts, setLeadCounts] = useState<Record<string, number>>({});
  // This ASA's own id (when logged in as ASA) — used to scope the list to the
  // agents assigned to them. Empty for ABA/super.
  const [selfAsaId, setSelfAsaId] = useState<string>("");

  // Normalize the (pending) asa_id field, which may arrive as a string or an
  // array of IDs (role IDs elsewhere in the system are arrays). Empty = unassigned.
  const readAsaId = (raw: any): string => {
    if (!raw) return "";
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v ? String(v) : "";
  };

  const mapAgent = (item: any): Agent => ({
    id: item._id,
    agentId: item.agent_id,
    agencyId: item.agency?._id,
    firstName: item.first_name,
    lastName: item.last_name,
    company: item.company_name || item.agency?.name || "",
    email: item.email_id,
    phone: `${item.mobile_number}`,
    username: item.email_id,
    type: item.agent_type === "internal" ? "Internal" : "External",
    status: item.is_active ? "Active" : "Inactive",
    photo: item.profile_pic,
    createdAt: item.created_at || item.createdAt || "",
    // Backend stores the assignment as `assigned_asa_id` (string); some views
    // also expose an `asa_id` array. Prefer the string, fall back to the array.
    asaId: readAsaId(item.assigned_asa_id || item.asa_id),
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // Resolve the agency for the logged-in role (ABA or ASA).
        // `any` so it threads into the JS api helpers without TS friction.
        let agencyId: any;
        if (UserService.hasRole("agency_business_admin")) {
          const res: any = await getClientIdByClientManger(
            UserService.getUseremail(),
          );
          agencyId = res?.agency_business_admin?.agency_id;
        } else if (UserService.hasRole("agency_system_admin")) {
          const res: any = await getClientAdminByEmailId(
            UserService.getUseremail(),
          );
          agencyId = res?.agency_system_admin?.agency_id;
          if (alive) setSelfAsaId(res?.agency_system_admin?._id || "");
        }
        if (!agencyId) {
          if (alive) {
            setAgents([]);
            setLoading(false);
          }
          return;
        }

        // Agents + the agency's System Admins (for the Assign dropdown and to
        // resolve asa_id → admin name in the Assigned tab) + the customer list
        // (to count leads per agent). ASA/customer lists are best-effort.
        const [agentsRes, asaRes, custRes] = await Promise.all([
          getAllAgent(0, 10000, agencyId),
          getSystemAdminList(agencyId).catch(() => null),
          getCustomerList(0, 10000, agencyId).catch(() => null),
        ]);

        const mapped: Agent[] = (
          (agentsRes as any)?.re_agent_list || []
        ).map(mapAgent);
        const asas: AsaOption[] = (
          (asaRes as any)?.agency_system_admin_list || []
        ).map((a: any) => ({
          id: a._id,
          name:
            `${a.first_name || ""} ${a.last_name || ""}`.trim() ||
            a.email_id ||
            "System Admin",
        }));

        // Leads per agent: a customer's agent_id is an array of assigned agent
        // (Mongo) ids; tally each occurrence.
        const counts: Record<string, number> = {};
        ((custRes as any)?.re_customer_list || []).forEach((c: any) => {
          (c.agent_id || []).forEach((id: string) => {
            if (id && id.trim()) counts[id] = (counts[id] || 0) + 1;
          });
        });

        if (alive) {
          setAgents(mapped);
          setAsaList(asas);
          setLeadCounts(counts);
          setLoading(false);
        }
      } catch (_e) {
        if (alive) {
          setAgents([]);
          setLoading(false);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleSave = async (updatedData: Partial<Agent>) => {
    if (editingAgent) {
      const apiPayload = {
        first_name: updatedData.firstName,
        last_name: updatedData.lastName,
        email_id: updatedData.email,
        mobile_number: updatedData.phone,
      };

      await updateAgent(apiPayload, editingAgent.id);

      setAgents(
        agents.map((a) =>
          a.id === editingAgent.id ? { ...a, ...updatedData } : a,
        ),
      );
    }
    setEditingAgent(null);
  };

  const matchesSearch = (agent: Agent) => {
    const searchLower = search.toLowerCase();
    const fullName = `${agent.firstName} ${agent.lastName}`.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      agent.phone.toLowerCase().includes(searchLower) ||
      agent.email.toLowerCase().includes(searchLower) ||
      agent.company.toLowerCase().includes(searchLower) ||
      agent.type.toLowerCase().includes(searchLower) ||
      agent.agentId.toLowerCase().includes(searchLower)
    );
  };

  // Per the role matrix:
  //   ABA / super → both Unassigned and Assigned tabs (+ create, edit, assign)
  //   ASA          → only the agents assigned to them (no tabs, no assign)
  const isABA =
    UserService.hasRole("agency_business_admin") ||
    UserService.hasRole("super_admin");
  const isASAOnly = UserService.hasRole("agency_system_admin") && !isABA;

  // Split by assignment to a System Admin (asa_id). Until Nivedita's asa_id
  // field lands every agent reads as unassigned, so the Assigned tab is empty.
  const assignedAgents = agents.filter((a) => a.asaId);
  const unassignedAgents = agents.filter((a) => !a.asaId);

  // For an ASA: strictly scope to their own agents (asaId === self). No
  // fallback — if none are assigned the list is empty and shows the
  // "contact your Business Admin" message.
  const asaOwnAgents = agents.filter((a) => a.asaId === selfAsaId);

  const baseAgents = isASAOnly
    ? asaOwnAgents
    : activeTab === "assigned"
      ? assignedAgents
      : unassignedAgents;
  const viewAgents = baseAgents.filter(matchesSearch);

  // Column visibility — the System Admin and Assign columns are ABA-only and
  // mutually exclusive per tab. Base columns = 8 (ID, Name, Type, Email, Phone,
  // Leads, Created, Edit).
  const showSystemAdminCol = isABA && activeTab === "assigned";
  const showAssignCol = isABA && activeTab === "unassigned";
  const colCount = 8 + (showSystemAdminCol ? 1 : 0) + (showAssignCol ? 1 : 0);

  // Empty-state copy + whether to offer an "Add New Agent" CTA, per context.
  const emptyState = search
    ? {
        title: "No matching agents",
        subtitle: "Try a different name, email, phone, or agent ID.",
        showAdd: false,
      }
    : isASAOnly
      ? {
          title: "No agents assigned to you yet",
          subtitle:
            "Your Business Admin hasn't assigned any agents to you yet. Reach out to them to get agents assigned — or add a new agent yourself.",
          showAdd: true,
        }
      : activeTab === "assigned"
        ? {
            title: "No assigned agents",
            subtitle:
              "Agents you assign to a System Admin will appear here.",
            showAdd: false,
          }
        : {
            title: "No unassigned agents",
            subtitle: "Every agent is currently assigned to a System Admin.",
            showAdd: false,
          };

  const asaName = (id?: string) =>
    asaList.find((x) => x.id === id)?.name || "Unknown";

  const fmtDate = (iso?: string) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleAssign = async (agent: Agent, asaId: string) => {
    // The agent update body takes `assigned_asa_id` (a single ASA id string) —
    // confirmed against the backend OpenAPI spec (EditREAgent schema).
    try {
      await updateAgent({ assigned_asa_id: asaId }, agent.id);
    } catch (_e) {
      /* surfaced via the agent staying in Unassigned on failure */
    }
    setAgents((prev) =>
      prev.map((a) => (a.id === agent.id ? { ...a, asaId } : a)),
    );
    setAssigningAgent(null);
    setActiveTab("assigned");
  };

  const initials = (a: Agent) =>
    `${a.firstName?.[0] || ""}${a.lastName?.[0] || a.firstName?.[1] || ""}`.toUpperCase() ||
    "NA";

  if (showPrompt) {
    return (
      <DesktopModePrompt
        message="This page contains agent management tables and data that are best viewed in desktop mode. Switch to desktop view for the best experience."
        onAccept={accept}
        onGoBack={() => {
          dismiss();
          navigate(slug ? `/c/${slug}` : "/");
        }}
      />
    );
  }

  return (
    <div className="font-inter h-full bg-gradient-to-b from-[#1C2C3A] to-[#1E4F52] p-3 sm:p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-1 text-sm">
          {!UserService.hasRole("customer") && (
            <>
              <button
                type="button"
                onClick={() => navigate(slug ? `/c/${slug}` : "/")}
                className="text-[#889DB2] hover:underline"
              >
                Home
              </button>
              <span className="text-[#889DB2] mx-1">›</span>
            </>
          )}
          <span className="text-white font-medium">
            My Agents ({isASAOnly ? asaOwnAgents.length : agents.length})
          </span>
        </div>
        <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 sm:pl-12 w-full sm:w-64 lg:w-85 px-3 py-2 text-sm sm:text-base text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors placeholder:text-[#889DB2]"
            />
            <Icon
              name="ic-search"
              className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-4 h-4"
            />
          </div>

          <AddButton
            label="Add New Agent"
            navigateTo="/add_agent"
            iconClassName="h-3 w-3"
          />
        </div>
      </div>

      {/* Assigned / Unassigned tabs — ABA/super only; an ASA sees only their own agents */}
      {isABA && (
        <div className="mb-4 flex items-center gap-2">
          {(
          [
            {
              key: "assigned",
              label: "Assigned Agents",
              count: assignedAgents.length,
            },
            {
              key: "unassigned",
              label: "Unassigned Agents",
              count: unassignedAgents.length,
            },
          ] as const
        ).map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? "border-[#34D399] text-[#34D399]"
                  : "border-[#2C4D5A] text-[#9FB8AF] hover:text-white hover:border-[#3B5C6A]"
              }`}
            >
              {t.label}
              <span
                className={`inline-flex min-w-[20px] h-5 items-center justify-center rounded-full px-1.5 text-xs ${
                  active
                    ? "bg-[#34D399] text-[#0B2027]"
                    : "bg-white/10 text-[#9FB8AF]"
                }`}
              >
                {t.count}
              </span>
            </button>
          );
          })}
        </div>
      )}

      {/* Row table */}
      <div className="bg-[#15333D] border border-[#2C4D5A] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] border-collapse text-left">
            <thead>
              <tr className="bg-[#11272E] text-[11px] uppercase tracking-wide text-[#7C97A8]">
                <th className="px-4 py-3 font-medium">Agent ID</th>
                <th className="px-4 py-3 font-medium">Agent Name</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Email ID</th>
                <th className="px-4 py-3 font-medium">Phone Number</th>
                <th className="px-4 py-3 font-medium text-center">Leads</th>
                {showSystemAdminCol && (
                  <th className="px-4 py-3 font-medium">System Admin</th>
                )}
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium text-center">Edit</th>
                {showAssignCol && (
                  <th className="px-4 py-3 font-medium text-center">Assign</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={colCount} className="py-16 text-center">
                    <div className="inline-block w-10 h-10 border-4 border-[#34D399] border-t-transparent rounded-full animate-spin" />
                  </td>
                </tr>
              ) : viewAgents.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="py-14">
                    <div className="flex flex-col items-center justify-center text-center px-6">
                      <div className="w-20 h-20 mb-4 rounded-full bg-[#11272E] border border-[#2C4D5A] flex items-center justify-center">
                        <Icon
                          name="ic-client-agents"
                          className="w-11 h-11 opacity-90"
                        />
                      </div>
                      <h3 className="text-white text-base font-semibold mb-1.5">
                        {emptyState.title}
                      </h3>
                      <p className="text-[#7C97A8] text-sm max-w-md leading-relaxed">
                        {emptyState.subtitle}
                      </p>
                      {emptyState.showAdd && (
                        <button
                          type="button"
                          onClick={() => navigate("/add_agent")}
                          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#008564] hover:bg-[#0a9b76] text-white text-sm font-medium px-4 py-2 transition-colors"
                        >
                          <Icon name="ic-plus" className="w-3.5 h-3.5" />
                          Add New Agent
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                viewAgents.map((agent) => (
                  <tr
                    key={agent.id}
                    className="border-t border-[#22323D] hover:bg-[#1A2F38] transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-[#9FB8AF] whitespace-nowrap">
                      {agent.agentId}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#34D399] to-[#059669] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                          {agent.photo ? (
                            <img
                              src={agent.photo}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            initials(agent)
                          )}
                        </div>
                        <span className="text-sm text-white font-medium whitespace-nowrap">
                          {toTitleCase(agent.firstName)}{" "}
                          {toTitleCase(agent.lastName)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          agent.type === "Internal"
                            ? "bg-[#34D399]/15 text-[#34D399]"
                            : "bg-[#60A5FA]/15 text-[#7DB6FB]"
                        }`}
                      >
                        {agent.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#C7D6DD] whitespace-nowrap">
                      {agent.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#C7D6DD] whitespace-nowrap">
                      {agent.phone}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex min-w-[24px] justify-center rounded-md bg-white/5 px-2 py-0.5 text-xs text-[#C7D6DD]">
                        {leadCounts[agent.id] || 0}
                      </span>
                    </td>
                    {showSystemAdminCol && (
                      <td className="px-4 py-3 text-sm text-[#C7D6DD] whitespace-nowrap">
                        {asaName(agent.asaId)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-[#9FB8AF] whitespace-nowrap">
                      {fmtDate(agent.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/add_agent?edit=${agent.id}`)
                        }
                        title="Edit agent"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#9FB8AF] hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <Icon name="ic-edit" className="w-4 h-4" />
                      </button>
                    </td>
                    {showAssignCol && (
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setAssigningAgent(agent)}
                          className="inline-flex items-center gap-1 rounded-md bg-[#008564] hover:bg-[#0a9b76] text-white text-xs font-medium px-3 py-1.5 transition-colors"
                        >
                          Assign
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingAgent && (
        <EditAgentModal
          agent={editingAgent}
          onClose={() => setEditingAgent(null)}
          onSave={handleSave}
        />
      )}

      {assigningAgent && (
        <AssignAgentModal
          agent={assigningAgent}
          asaOptions={asaList}
          onClose={() => setAssigningAgent(null)}
          onAssign={(asaId) => handleAssign(assigningAgent, asaId)}
        />
      )}
    </div>
  );
};

interface EditAgentModalProps {
  agent: Agent;
  onClose: () => void;
  onSave: (data: Partial<Agent>) => void;
}

const EditAgentModal: React.FC<EditAgentModalProps> = ({
  agent,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    firstName: agent.firstName,
    lastName: agent.lastName,
    email: agent.email,
    phone: agent.phone,
  });

  const handleSave = () => {
    console.log("edit agent 111", formData);
    onSave(formData);
  };
  console.log("edit agent22", agent);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="flex flex-col gap-4 bg-[#1C2C3A] rounded-lg pb-6 pt-8 sm:pt-10 px-4 sm:px-6 w-full max-w-[95vw] sm:max-w-lg md:max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardBorder
          title="Edit Agent"
          borderColor="border-[#2E4051]"
          bgColor="bg-[#1C2C3A]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-gray-400 text-sm">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                className="w-full bg-[#2E4051] text-white px-4 py-2 rounded-lg mt-1 shadow-sm border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm">Last name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className="w-full bg-[#2E4051] text-white px-4 py-2 rounded-lg mt-1 shadow-sm border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm">Email</label>
              <input
                type="email"
                value={formData.email}
                readOnly
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full bg-[#2E4051] text-white px-4 py-2 rounded-lg mt-1 shadow-sm border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm">Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                readOnly
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="w-full bg-[#2E4051] text-white px-4 py-2 rounded-lg mt-1 shadow-sm border-none focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors"
              />
            </div>
          </div>
        </CardBorder>

        <div className="flex justify-center gap-3 sm:gap-4">
          <button
            onClick={onClose}
            className="bg-[#2E4051] text-[#fff] text-base sm:text-xl px-4 sm:px-6 py-2 rounded-lg"
            style={{ filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.25))" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-[#008564] text-white text-base sm:text-xl px-4 sm:px-6 py-2 rounded-lg"
            style={{ filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.25))" }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

interface AssignAgentModalProps {
  agent: Agent;
  asaOptions: AsaOption[];
  onClose: () => void;
  onAssign: (asaId: string) => void;
}

const AssignAgentModal: React.FC<AssignAgentModalProps> = ({
  agent,
  asaOptions,
  onClose,
  onAssign,
}) => {
  const [selected, setSelected] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    if (!selected) return;
    setSaving(true);
    await onAssign(selected);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-[#15333D] border border-[#2C4D5A] rounded-xl w-full max-w-md p-5 sm:p-6">
        <h3 className="text-white text-lg font-semibold">Assign to System Admin</h3>
        <p className="text-[#9FB8AF] text-sm mt-1">
          {toTitleCase(agent.firstName)} {toTitleCase(agent.lastName)}
          <span className="text-[#7C97A8]"> · {agent.agentId}</span>
        </p>

        {asaOptions.length === 0 ? (
          <div className="mt-5 rounded-lg border border-[#2C4D5A] bg-[#11272E] p-4 text-sm text-[#9FB8AF]">
            No System Admins exist in this agency yet. Create one first, then
            assign the agent.
          </div>
        ) : (
          <div className="mt-5 max-h-64 overflow-y-auto flex flex-col gap-1.5">
            {asaOptions.map((asa) => {
              const active = selected === asa.id;
              return (
                <button
                  key={asa.id}
                  type="button"
                  onClick={() => setSelected(asa.id)}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    active
                      ? "border-[#34D399] bg-[#34D399]/10"
                      : "border-[#2C4D5A] hover:border-[#3B5C6A] hover:bg-white/5"
                  }`}
                >
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#34D399] to-[#059669] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {asa.name
                      .split(" ")
                      .map((w) => w[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase() || "SA"}
                  </span>
                  <span
                    className={`text-sm ${active ? "text-white" : "text-[#C7D6DD]"}`}
                  >
                    {asa.name}
                  </span>
                  {active && (
                    <span className="ml-auto text-[#34D399] text-sm">✓</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-[#C7D6DD] bg-white/5 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!selected || saving}
            className="px-4 py-2 rounded-lg text-sm text-white bg-[#008564] hover:bg-[#0a9b76] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Assigning…" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentList;
